import {
	FeatureEnum,
	PropertyTypeEnum,
	propertyTypesSlugByValue,
	propertyTypesValueBySlug
} from '../CRMEnums';

type ApiTarget = 'listing' | 'clusters';
type Placement = 'query' | 'path-and-query' | 'context';
type PathRole =
	| 'transaction'
	| 'location'
	| 'address'
	| 'propertyType'
	| 'state'
	| 'city'
	| 'region';
type Presence = 'nonempty' | 'positive' | 'true' | 'boolean' | 'never';
interface DefinitionOptions {
	applied?: Presence;
	placement?: Placement;
	role?: PathRole;
	targets?: readonly ApiTarget[];
}
interface FilterDefinition<Value> {
	kind: 'string' | 'number' | 'array' | 'boolean';
	applied: Presence;
	placement: Placement;
	role?: PathRole;
	targets: readonly ApiTarget[];
	normalize(input: unknown): Value | undefined;
	read(values: string[]): Value | undefined;
	write(input: unknown): string | undefined;
}
function definition<Value>(
	kind: FilterDefinition<Value>['kind'],
	normalize: (input: unknown) => Value | undefined,
	options: DefinitionOptions = {}
): FilterDefinition<Value> {
	return {
		kind,
		normalize,
		applied:
			options.applied ??
			(kind === 'number'
				? 'positive'
				: kind === 'boolean'
					? 'boolean'
					: 'nonempty'),
		placement: options.placement ?? 'query',
		role: options.role,
		targets: options.targets ?? ['listing', 'clusters'],
		read: values => {
			if (kind === 'array')
				return normalize(
					values.flatMap(value =>
						value.startsWith('~[')
							? parseArray(value.slice(1))
							: value.split(',')
					)
				);
			return values.map(normalize).find(value => value !== undefined);
		},
		write(input) {
			const value = normalize(input);
			if (value === undefined) return;
			if (Array.isArray(value))
				return value.some(
					item => String(item).includes(',') || String(item).startsWith('~[')
				)
					? '~' + JSON.stringify(value)
					: value.join(',');
			return String(value);
		}
	};
}
function parseArray(value: string): unknown[] {
	try {
		const parsed: unknown = JSON.parse(value);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}
const stringValue = (input: unknown): string | undefined =>
	typeof input === 'string' && input.trim() ? input : undefined;
const numberValue = (input: unknown): number | undefined => {
	if (typeof input !== 'number' && (typeof input !== 'string' || !input.trim()))
		return;
	const value = Number(input);
	return Number.isFinite(value) ? value : undefined;
};
const booleanValue = (input: unknown): boolean | undefined =>
	input === true || input === 'true'
		? true
		: input === false || input === 'false'
			? false
			: undefined;
const enumValue =
	<Value extends string>(values: readonly Value[]) =>
	(input: unknown): Value | undefined =>
		values.find(value => value === input);
const text = (options?: DefinitionOptions) =>
	definition('string', stringValue, options);
const numeric = (options?: DefinitionOptions) =>
	definition('number', numberValue, options);
const flag = (options?: DefinitionOptions) =>
	definition('boolean', booleanValue, options);
const trueFlag = (options?: DefinitionOptions) =>
	definition(
		'boolean',
		(input: unknown): true | undefined =>
			booleanValue(input) === true ? true : undefined,
		{ applied: 'true', ...options }
	);
const enumeration = <Value extends string>(
	values: readonly Value[],
	options?: DefinitionOptions
) => definition('string', enumValue(values), options);
const array = <Value>(
	item: (input: unknown) => Value | undefined,
	options?: DefinitionOptions
) =>
	definition(
		'array',
		(input: unknown): Value[] | undefined => {
			if (!Array.isArray(input)) return;
			const values = input.flatMap(value => {
				const normalized = item(value);
				return normalized === undefined ? [] : [normalized];
			});
			return values.length ? values : undefined;
		},
		options
	);
function propertyTypes(options: DefinitionOptions) {
	const item = (input: unknown) =>
		enumValue(Object.values(PropertyTypeEnum))(input) ??
		Object.entries(propertyTypesValueBySlug).find(
			([slug]) => slug === input
		)?.[1];
	const field = array(item, options);
	return {
		...field,
		write: (input: unknown) =>
			field
				.normalize(input)
				?.map(value => propertyTypesSlugByValue[value])
				.join(',')
	};
}
const strings = (options?: DefinitionOptions) => array(stringValue, options);

export const propertyFilterDefinitions = {
	transactionType: enumeration(['sell', 'rent'] as const, {
		placement: 'path-and-query',
		role: 'transaction'
	}),
	orderBy: enumeration(['asc', 'desc'] as const, { targets: ['listing'] }),
	sortBy: enumeration(
		[
			'images_count',
			'asking_price',
			'area_price',
			'area',
			'publication_date',
			'portal_home_select',
			'default_ordenation',
			'distance',
			'wishlist_recent',
			'wishlist_oldest'
		] as const,
		{ targets: ['listing'] }
	),
	searchTerm: text(),
	isExclusive: flag(),
	// Open house not cancelled nor ended; the API only accepts `true`, omit to disable.
	hasOpenHouse: trueFlag(),
	features: array(enumValue(Object.values(FeatureEnum))),
	propertyTypes: propertyTypes({
		placement: 'path-and-query',
		role: 'propertyType'
	}),
	commercialIds: strings({ targets: ['listing'] }),
	regions: strings({ placement: 'path-and-query', role: 'region' }),
	cities: strings({ placement: 'path-and-query', role: 'city' }),
	states: strings({ placement: 'path-and-query', role: 'state' }),
	macroregions: strings(),
	pluscodesGroup: strings(),
	h3Ids: strings({ targets: ['listing'] }),
	minArea: numeric(),
	maxArea: numeric(),
	minAskingPrice: numeric(),
	maxAskingPrice: numeric(),
	minRentPrice: numeric(),
	maxRentPrice: numeric(),
	minSquareMeterPrice: numeric(),
	maxSquareMeterPrice: numeric(),
	minCondoFee: numeric(),
	maxCondoFee: numeric(),
	minTax: numeric(),
	maxTax: numeric(),
	suites: numeric(),
	bedrooms: numeric(),
	bathrooms: numeric(),
	parkingSpots: numeric(),
	// A point (POI or address) makes the API sort by distance and compute poi_distance_time.
	poiLocationLat: numeric(),
	poiLocationLng: numeric(),
	zoom: numeric(),
	onlyPluscodes: flag({ applied: 'true' }),
	includeH3Clusters: flag({ applied: 'true' }),
	locationSlug: text({ placement: 'path-and-query', role: 'location' }),
	addressSlug: text({ placement: 'path-and-query', role: 'address' }),
	// The API resolves the agent's public slug and returns only that agent's listings.
	agentSlug: text(),
	// Map bounds as 'west,south,east,north'.
	bounds: text(),
	countPluscodes: strings({ applied: 'never', targets: ['listing'] }),
	excludeProperties: strings({ applied: 'never', targets: ['listing'] }),
	isHeroSection: flag({ applied: 'never', targets: ['listing'] }),
	// Curated set without price/regions; otherwise >= R$ 15M in premium regions.
	isChosenForYou: flag({ applied: 'never' }),
	showHideProperties: flag({ applied: 'never' }),
	hasVideos: flag({ applied: 'never' }),
	excludeFeaturedVideos: flag({ applied: 'never', targets: ['listing'] }),
	similarTo: text({ applied: 'never', targets: ['listing'] }),
	curatedListId: text({ applied: 'never' }),
	wishlistId: text({ placement: 'context', applied: 'never' }),
	// Lets the API flag each property already in this user's wishlists.
	markUserWishlistsId: text({
		placement: 'context',
		applied: 'never',
		targets: ['listing']
	}),
	excludeDuplicationGroup: text({ applied: 'never' })
};
export type DefinedPropertyFilters = {
	[K in keyof typeof propertyFilterDefinitions]?: ReturnType<
		(typeof propertyFilterDefinitions)[K]['normalize']
	>;
};
export type ReadonlyPropertyFilters = {
	readonly [K in keyof DefinedPropertyFilters]: NonNullable<
		DefinedPropertyFilters[K]
	> extends readonly (infer Item)[]
		? readonly Item[]
		: DefinedPropertyFilters[K];
};
export type PropertyFilterPatch = {
	[K in keyof DefinedPropertyFilters]?: DefinedPropertyFilters[K] | null;
};

export function isPropertyFilterKey(
	key: string
): key is keyof DefinedPropertyFilters {
	return Object.hasOwn(propertyFilterDefinitions, key);
}
export function normalizePropertyFilters(
	input: object
): DefinedPropertyFilters {
	const result: DefinedPropertyFilters = {};
	for (const [key, field] of Object.entries(propertyFilterDefinitions)) {
		const value = field.normalize(Reflect.get(input, key));
		if (value !== undefined) Reflect.set(result, key, value);
	}
	return result;
}
export function readPropertyFilterQuery(
	query: URLSearchParams
): DefinedPropertyFilters {
	const result: DefinedPropertyFilters = {};
	for (const [key, field] of Object.entries(propertyFilterDefinitions)) {
		if (field.placement === 'context') continue;
		const value = field.read(query.getAll(key));
		if (value !== undefined) Reflect.set(result, key, value);
	}
	return result;
}
export function writePropertyFilterQuery(
	filters: ReadonlyPropertyFilters,
	includePathFields = false
): URLSearchParams {
	const query = new URLSearchParams();
	for (const [key, field] of Object.entries(propertyFilterDefinitions)) {
		if (
			field.placement === 'context' ||
			(!includePathFields && field.placement === 'path-and-query')
		)
			continue;
		const value = field.write(Reflect.get(filters, key));
		if (value !== undefined) query.set(key, value);
	}
	return query;
}
export function projectPropertyFilters(
	filters: ReadonlyPropertyFilters,
	target: ApiTarget
): DefinedPropertyFilters {
	const normalized = normalizePropertyFilters(filters);
	for (const [key, field] of Object.entries(propertyFilterDefinitions)) {
		if (!field.targets.includes(target))
			Reflect.deleteProperty(normalized, key);
	}
	return normalized;
}
export function getAppliedPropertyFilterKeys(
	filters: ReadonlyPropertyFilters
): (keyof DefinedPropertyFilters)[] {
	return Object.keys(filters)
		.filter(isPropertyFilterKey)
		.filter(key => {
			const value = filters[key];
			switch (propertyFilterDefinitions[key].applied) {
				case 'nonempty':
					return typeof value === 'string'
						? value.length > 0
						: Array.isArray(value) && value.length > 0;
				case 'positive':
					return typeof value === 'number' && value > 0;
				case 'true':
					return value === true;
				case 'boolean':
					return typeof value === 'boolean';
				case 'never':
					return false;
			}
		});
}
